import { EMPLOYMENT_TYPE } from "./constants";

/** Straight-line (haversine) distance in kilometres. */
export function distanceKm(
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
): number {
	const R = 6371;
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLon / 2) ** 2;
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Where the Visit is served: its Region and coordinates. */
export type SearchOrigin = {
	city?: string | null;
	latitude?: number | null;
	longitude?: number | null;
};

export type RankedTherapist<T> = {
	therapist: T;
	/** From the therapist's Domisili to the Visit; null when either end has no coordinates. */
	distanceKm: number | null;
	/** Domisili in another Region than the Visit. False when either Region is unknown. */
	outsideRegion: boolean;
};

export type RankedCityGroup<T> = {
	city: string;
	therapists: RankedTherapist<T>[];
};

/** The slice of a search result ranking reads; TherapistFeasible satisfies it. */
type Rankable = {
	employmentType?: string | null;
	activeAddress?: {
		latitude?: number | null;
		longitude?: number | null;
		location?: { city?: string | null } | null;
	} | null;
};

const sameCity = (a?: string | null, b?: string | null) =>
	!!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const byDistance = (a: number | null, b: number | null) =>
	(a ?? Number.POSITIVE_INFINITY) - (b ?? Number.POSITIVE_INFINITY);

/**
 * Orders a name search the way an assignment should be weighed: the Visit's own Region
 * first, then the other cities nearest-first by median distance; inside a city, KARPIS before Mitra, then
 * nearest. Without an origin it only groups by city, keeping the server's order.
 */
export function rankTherapistSearchResults<T extends Rankable>(
	therapists: T[],
	origin: SearchOrigin | null | undefined,
	unknownCityLabel: string,
): RankedCityGroup<T>[] {
	const hasCoords = !!origin?.latitude && !!origin?.longitude;
	const groups = new Map<string, RankedTherapist<T>[]>();

	for (const therapist of therapists) {
		const address = therapist.activeAddress;
		const city = address?.location?.city || unknownCityLabel;
		const distance =
			hasCoords && address?.latitude && address?.longitude
				? distanceKm(
						Number(origin?.latitude),
						Number(origin?.longitude),
						Number(address.latitude),
						Number(address.longitude),
					)
				: null;
		const ranked: RankedTherapist<T> = {
			therapist,
			distanceKm: distance,
			outsideRegion:
				!!origin?.city &&
				!!address?.location?.city &&
				!sameCity(origin.city, address.location.city),
		};
		groups.set(city, [...(groups.get(city) ?? []), ranked]);
	}

	const result = [...groups.entries()].map(([city, members]) => ({
		city,
		therapists: origin
			? [...members].sort(
					(a, b) =>
						Number(a.therapist.employmentType !== EMPLOYMENT_TYPE.KARPIS) -
							Number(b.therapist.employmentType !== EMPLOYMENT_TYPE.KARPIS) ||
						byDistance(a.distanceKm, b.distanceKm),
				)
			: members,
	}));
	if (!origin) return result;

	// A city sits at the median of its therapists' distances, so one stray address on
	// file cannot pull a whole city up the list the way its nearest member would.
	const median = (group: RankedCityGroup<T>) => {
		const known = group.therapists
			.map((t) => t.distanceKm)
			.filter((d): d is number => d !== null)
			.sort((a, b) => a - b);
		return known.length
			? known[Math.floor((known.length - 1) / 2)]
			: Number.POSITIVE_INFINITY;
	};
	return result.sort(
		(a, b) =>
			Number(!sameCity(a.city, origin.city)) -
				Number(!sameCity(b.city, origin.city)) || median(a) - median(b),
	);
}
